import { useState, FormEvent, useEffect, useMemo } from "react";
import ModalComponent from "components/modal";
import { emptyExpenseRow, ExpenseItem } from "../detail";
import ActionButton from "components/table/action-btn";
import { IconInfoCircle, IconMinus } from "@tabler/icons-react";
import { getLocationsByUser } from "api/location";
import { getTemplatesByLocation } from "api/template";
import { LocationType, Template } from "types/dict";
import { useSharedState } from "state";
import { createPaymentRequest, uploadPaymentFiles } from "api/payment";
import { getBudgetBalance } from "api/budget_request";
import FormattedNumberInput from "components/input/formatted-number";
import moment from "moment";

type FormErrors = Record<string, boolean>;
type FormRecords = Record<string, string>;

const REQUIRED_FIELDS = [
  "location_id",
  "company_id",
  "department_id",
  "template_id",
  "contragent",
  "project",
  "year",
  "title",
  "dogovor",
  "vid_oplaty",
  "opisanie_rasxoda",
  "file",
] as const;

const PAYMENT_TYPES = [
  {
    value: "",
    label: "Вид оплаты",
    disabled: true,
  },
  {
    value: "Безналичная оплата",
    label: "Безналичная оплата",
    disabled: false,
  },
  {
    value: "Наличная оплата",
    label: "Наличная оплата",
    disabled: false,
  },
];

const getInputErrorMessage = (fieldId: string) =>
  fieldId === "file" ? "Добавьте договор" : "Введите корректное значение";

export default function NewPayment() {
  const {
    user,
    isAdmin,
    budgetTypes,
    getBudgetTypesData,
    getPaymentsData,
    companies,
    getCompaniesData,
    departments,
    getDepartmentsData,
    levels,
    getLevelsData,
    notify,
  } = useSharedState();

  const [formRecords, setFormRecords] = useState<FormRecords>({ year: moment().year().toString() });
  const [expenseTable, setExpenseTable] = useState<ExpenseItem[]>([emptyExpenseRow]);

  const [totalBudgetBalance, setTotalBudgetBalance] = useState<number>(0);

  const [errors, setErrors] = useState<FormErrors>({});
  const [fileList, setFileList] = useState<FileList | null>(null);

  const [userTemplates, setUserTemplates] = useState<Template[]>([]);

  const [isBudgetCalculated, setBudgetCalculated] = useState<boolean>(false);
  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);

  const totalSum = useMemo(
    () => expenseTable.reduce((acc, cur) => acc + (cur.quantity || 0) * (cur.price || 0), 0),
    [expenseTable]
  );

  const zeroLevels = useMemo(() => {
    return levels.filter((lvl) => lvl.parent_budget_type_id === null);
  }, [levels]);

  const chosenLevels = useMemo(() => {
    return (mainLevelId: number) => levels.filter((lvl) => lvl.parent_budget_type_id === Number(mainLevelId));
  }, [levels]);

  const maxLevels = useMemo(() => {
    // Find the largest max_levels in expenseTable by budget.max_levels property
    return Math.max(
      0,
      ...expenseTable.map((item) => {
        // Find the matching level by budget_type_id
        const level = zeroLevels.find((lvl) => lvl.budget_type_id === Number(item.budget));
        return level?.max_levels ?? 0;
      })
    );
  }, [expenseTable, zeroLevels]);

  const isBudgetCalculable = useMemo(() => {
    return !!formRecords.location_id && !!formRecords.company_id && !!formRecords.department_id && !!formRecords.year;
  }, [formRecords]);

  const paymentFields = [
    {
      id: "location_id",
      title: "Филиал",
      type: "select",
      placeholder: undefined,
      options: [
        { value: "", label: "Выберите филиал", disabled: true },
        ...user.locations
          .filter((loc) => loc.status)
          .map((loc) => ({ value: loc.location_id, label: loc.location, disabled: false })),
      ],
      disabled: undefined,
    },
    {
      id: "company_id",
      title: "Юр. лицо",
      type: "select",
      placeholder: undefined,
      options: [
        { value: "", label: "Выберите юр. лицо", disabled: true },
        ...companies
          .filter((com) => com.location_id === +formRecords.location_id && com.status)
          .map((com) => ({ value: com.company_id, label: com.company, disabled: false })),
      ],
      disabled: formRecords.location_id ? false : true,
    },
    {
      id: "department_id",
      title: "Отдел",
      type: "select",
      placeholder: undefined,
      options: [
        { value: "", label: "Выберите отдел", disabled: true },
        ...departments
          .filter((dep) => dep.status)
          .map((dep) => ({ value: dep.department_id, label: dep.department, disabled: false })),
      ],
      disabled: formRecords.location_id ? false : true,
    },
    {
      id: "template_id",
      title: "Шаблон",
      type: "select",
      placeholder: undefined,
      options: [
        {
          value: "",
          label: formRecords.location_id ? "Выберите шаблон" : "Сначала выберите филиал",
          disabled: true,
        },
        ...userTemplates.map((temp) => ({ value: temp.template_id, label: temp.name, disabled: false })),
      ],
      disabled: formRecords.location_id ? false : true,
    },
    {
      id: "project",
      title: "Проект",
      type: "text",
      placeholder: "Проект",
      options: undefined,
      disabled: undefined,
    },
    {
      id: "year",
      title: "Год бюджета",
      type: "number",
      placeholder: "Год бюджета",
      options: undefined,
      disabled: undefined,
    },
    {
      id: "title",
      title: "Наименование",
      type: "text",
      placeholder: "Наименование",
      options: undefined,
      disabled: undefined,
    },
    {
      id: "contragent",
      title: "Контрагент",
      type: "text",
      placeholder: "Контрагент",
      options: undefined,
      disabled: undefined,
    },
    {
      id: "dogovor",
      title: "Номер договора",
      type: "text",
      placeholder: "Номер договора",
      options: undefined,
      disabled: undefined,
    },
    {
      id: "vid_oplaty",
      title: "Вид оплаты",
      type: "select",
      placeholder: undefined,
      options: PAYMENT_TYPES,
      disabled: undefined,
    },
    {
      id: "budget_summa",
      title: "Общая сумма бюджета",
      type: "number",
      placeholder: "Общая сумма бюджета",
      options: undefined,
      disabled: true,
    },
    {
      id: "ostatok_budgeta",
      title: "Общий остаток бюджета",
      type: "number",
      placeholder: "Общий остаток бюджета",
      options: undefined,
      disabled: true,
    },
    {
      id: "opisanie_rasxoda",
      title: "Описание расхода",
      type: "textarea",
      placeholder: "Описание расхода",
      options: undefined,
      disabled: undefined,
    },
    {
      id: "file",
      title: "Загрузить договоры",
      type: "file",
      placeholder: undefined,
      options: undefined,
      disabled: undefined,
    },
  ] as const;

  useEffect(() => {
    getBudgetTypesData();
    getLevelsData();
  }, []);

  useEffect(() => {
    if (formRecords.location_id) {
      getTemplatesData();
      getCompaniesData();
      getDepartmentsData();
    }
  }, [formRecords.location_id]);

  useEffect(() => {
    if (!isAdmin && formRecords.location_id) {
      const company = companies.find((c) => c.location_id === Number(formRecords.location_id));
      setFormRecords((prev) => ({ ...prev, company_id: company?.company_id.toString() || "" }));
      setFormRecords((prev) => ({ ...prev, department_id: user.result.department_id.toString() }));
    }
  }, [user, isAdmin, formRecords.location_id]);

  const getTemplatesData = async () => {
    try {
      const userTemplates = await getTemplatesByLocation("Оплаты", +formRecords.location_id);
      setUserTemplates(userTemplates);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении шаблонов");
    }
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      if (!formRecords[field]?.trim()) {
        newErrors[field] = true;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateExpenses = () => {
    return expenseTable.some(
      (expense) =>
        expense.name?.trim() && expense.quantity && expense.price && expense.budget && expense.budget_levels?.length
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const files = (e.target as HTMLInputElement).files;

    files && setFileList(files);

    setFormRecords((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const submitPayment = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !validateExpenses()) {
      !validateExpenses() && notify("Заполните все поля в таблице расходов", "danger");
      return;
    }

    setFormLoading(true);
    try {
      const paymentDetails: any = [];
      const formData = new FormData();

      expenseTable.forEach((expense) => {
        let detail = {
          "Наименование товара": expense.name,
          Количество: expense.quantity,
          Цена: expense.price,
          Сумма: (expense.quantity || 0) * (expense.price || 0),
          "Статья из бюджета/Вне бюджета": "",
          budget_levels: [Number(expense.budget!), ...expense.budget_levels!],
        };
        paymentDetails.push(detail);
      });

      const paymentForm = {
        location_id: +formRecords.location_id,
        company_id: +formRecords.company_id,
        department_id: +formRecords.department_id,
        year: Number(formRecords.year) || moment().year(),
        template_id: +formRecords.template_id,
        choosen_location: user.locations.find((item) => item.location_id === +formRecords.location_id)?.location,
        template_name: userTemplates.find((item) => item.template_id === +formRecords.template_id)?.name,
        contragent: formRecords.contragent,
        project: formRecords.project,
        title: formRecords.title,
        dogovor: formRecords.dogovor,
        details: paymentDetails,
        total_sum: totalSum,
        vid_oplaty: formRecords.vid_oplaty,
        budget_summa: totalBudgetBalance,
        ostatok_budgeta: totalBudgetBalance - totalSum,
        opisanie_rasxoda: formRecords.opisanie_rasxoda,
      };

      if (fileList) {
        Array.from(fileList).forEach((file) => {
          formData.append(`files`, file);
        });
      }

      const request_id = await createPaymentRequest(paymentForm);
      await uploadPaymentFiles(request_id, formData);
      notify("Заявка на оплату создана", "success");

      setFormSubmitted(true);
      getPaymentsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при создании заявки на оплату");
    } finally {
      setFormLoading(false);
    }
  };

  const handleAddRow = () => {
    setExpenseTable((prev) => [...prev, { ...emptyExpenseRow }]);
  };

  const handleDeleteRow = (index: number) => {
    setExpenseTable((prev) => prev.filter((_, i) => i !== index));
  };

  const handleExpenseChange = (index: number, field: keyof ExpenseItem, value: string, lvl_index?: number) => {
    setExpenseTable((prev) => {
      const newTable = [...prev];
      newTable[index] = {
        ...newTable[index],
        [field]:
          field === "budget_levels"
            ? Array.isArray(newTable[index][field])
              ? (newTable[index][field] = [
                  ...(newTable[index][field] as number[]).slice(0, lvl_index!),
                  Number(value),
                  ...(newTable[index][field] as number[]).slice(lvl_index! + 1),
                ])
              : (newTable[index][field] = [Number(value)])
            : field === "quantity" || field === "price" || field === "sum"
            ? value === ""
              ? undefined
              : Number(value)
            : value,
      };
      if (field === "budget") {
        newTable[index].budget_levels = [];
      }
      return newTable;
    });
  };

  const handleGetBudgetBalance = async () => {
    if (!formRecords.company_id || !formRecords.department_id || !formRecords.year) return;
    try {
      setFormLoading(true);

      const budgetsMap = new Map();
      // Create a map to store unique budget level combinations and their initial balances
      const budgetLevelMap = new Map();

      // Process each row sequentially
      for (let i = 0; i < expenseTable.length; i++) {
        const expense = expenseTable[i];
        if (!expense.budget || !expense.budget_levels?.length) continue;

        // Create a key for this combination of budget levels
        const levelKey = [expense.budget, ...expense.budget_levels].join("-");

        // If we already have this combination, use the remaining balance from previous calculation
        if (budgetLevelMap.has(levelKey)) {
          const prevBalance = budgetLevelMap.get(levelKey);
          // Update the expense table with the remaining balance
          setExpenseTable((prev) => {
            const newTable = [...prev];
            newTable[i] = {
              ...newTable[i],
              budget_balance: prevBalance,
            };
            // Update the map with the new remaining balance
            const newBalance = prevBalance - (expense.quantity || 0) * (expense.price || 0);
            budgetLevelMap.set(levelKey, newBalance);
            return newTable;
          });
        } else {
          // New combination - fetch the balance from API
          const balanceData = {
            company_id: +formRecords.company_id,
            department_id: +formRecords.department_id,
            year: +formRecords.year,
            budget_levels: [...expense.budget_levels.map(Number)],
          };

          const res: { remaining_budget_balance: number } = await getBudgetBalance(balanceData);

          // Store the remaining balance in the map
          const newBalance = res.remaining_budget_balance - (expense.quantity || 0) * (expense.price || 0);
          budgetsMap.set(levelKey, res.remaining_budget_balance);
          budgetLevelMap.set(levelKey, newBalance);

          // Update the expense table with the new balance
          setExpenseTable((prev) => {
            const newTable = [...prev];
            newTable[i] = {
              ...newTable[i],
              budget_balance: res.remaining_budget_balance,
            };
            return newTable;
          });
        }
      }

      setTotalBudgetBalance(Array.from(budgetsMap.values()).reduce((acc, val) => acc + val, 0));
      setBudgetCalculated(true);
      notify("Бюджет рассчитан", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при расчёте бюджета");
    } finally {
      setFormLoading(false);
    }
  };

  const expenseTableHeaders = [
    { header: "Наименование", narrow: false, wrap: false },
    { header: "Количество", narrow: false, wrap: true },
    { header: "Цена", narrow: false, wrap: false },
    { header: "Сумма", narrow: false, wrap: false },
    { header: "Название бюджета", narrow: false, wrap: false },
    ...(maxLevels > 0
      ? Array.from({ length: maxLevels }, (_, i) => ({
          header: `Уровень ${i + 1}`,
          required: true,
          narrow: false,
          wrap: false,
        }))
      : []),
    { header: "Сумма бюджета", narrow: false, wrap: false },
    { header: "Остаток бюджета", narrow: false, wrap: false },
    {
      header: (
        <button type="button" className="btn btn-ghost-primary" onClick={handleAddRow}>
          Добавить строку
        </button>
      ),
      narrow: true,
      wrap: false,
    },
  ];

  const renderField = ({ id, title, type, placeholder, options, disabled }: (typeof paymentFields)[number]) => {
    const Component = type === "textarea" ? "textarea" : "input";
    return (
      <div className={id === "opisanie_rasxoda" || id === "file" ? "col-lg-12" : "col-lg-4"} key={id}>
        <div className="mb-3">
          <label className={`form-label ${id === "budget_summa" || id === "ostatok_budgeta" ? "" : "required"}`}>
            {title}
          </label>
          <div className="input-icon mb-3">
            {type === "select" ? (
              <select
                className={`form-select ${errors[id] ? "is-invalid" : ""}`}
                name={id}
                defaultValue={""}
                value={formRecords[id]}
                onChange={handleInputChange}
                disabled={isFormLoading || disabled}
              >
                {options.map((opt, index) => (
                  <option key={index} value={opt.value} disabled={opt.disabled}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : type === "number" && id !== "year" ? (
              <FormattedNumberInput
                className={`form-control ${errors[id] ? "is-invalid" : ""}`}
                name={id}
                value={
                  id === "budget_summa"
                    ? totalBudgetBalance
                    : id === "ostatok_budgeta"
                    ? totalBudgetBalance - totalSum
                    : formRecords[id] === "" || formRecords[id] === undefined
                    ? null
                    : Number(formRecords[id])
                }
                onChange={handleInputChange}
                disabled={isFormLoading || disabled}
              />
            ) : (
              <Component
                type={type}
                className={`form-control ${errors[id] ? "is-invalid" : ""}`}
                name={id}
                placeholder={placeholder}
                value={formRecords[id] || ""}
                onChange={handleInputChange}
                disabled={isFormLoading || disabled}
                multiple={type === "file"}
              />
            )}

            {errors[id] && <div className="invalid-feedback">{getInputErrorMessage(id)}</div>}
            {isFormLoading && (
              <span className="input-icon-addon">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const newPaymentBody = (
    <form id="paymentForm" className="row" onSubmit={submitPayment}>
      {paymentFields.slice(0, 9).map(renderField)}

      <div className="col-12 mb-3">
        <label className="form-label required">Детали оплаты</label>
        <div className="card">
          <div className="table-responsive">
            <table className="table table-vcenter card-table">
              <thead>
                <tr>
                  {expenseTableHeaders.map((header, index) => (
                    <th className={`${header.narrow && "w-1"} ${header.wrap && "text-wrap"}`} key={index}>
                      {header.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {expenseTable?.map((expense, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: 200 }}
                        name="name"
                        value={expense.name}
                        onChange={(e) => handleExpenseChange(index, "name", e.target.value)}
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control"
                        style={{ width: 100 }}
                        name="quantity"
                        value={expense.quantity || null}
                        onChange={(e) => handleExpenseChange(index, "quantity", e.target.value)}
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control"
                        style={{ width: 100 }}
                        name="price"
                        value={expense.price || null}
                        onChange={(e) => handleExpenseChange(index, "price", e.target.value)}
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control"
                        style={{ width: 100 }}
                        name="sum"
                        value={(expense.quantity || 0) * (expense.price || 0) || null}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <select
                        className="form-select"
                        style={{ width: 200 }}
                        name="budget_type_id"
                        value={expense.budget || ""}
                        onChange={(e) => handleExpenseChange(index, "budget", e.target.value)}
                      >
                        {[
                          {
                            value: "",
                            label: "Выберите бюджет",
                            disabled: true,
                          },
                          ...zeroLevels
                            .filter((lvl) => lvl.status)
                            .map((lvl) => ({
                              label: lvl.budget_type!,
                              value: lvl.budget_type_id!,
                              disabled: false,
                            })),
                        ].map((opt, index) => (
                          <option key={index} value={opt.value} disabled={opt.disabled}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>

                    {(maxLevels > 0 ? Array.from({ length: maxLevels }, (_, i) => ({ num: i + 1 })) : []).map(
                      (lvl, idx) => (
                        <td key={idx}>
                          {lvl.num <=
                          (levels.find((lvl) => lvl.budget_type_id === Number(expenseTable[index].budget))
                            ?.max_levels || 0) ? (
                            <select
                              className="form-select"
                              style={{ width: 200 }}
                              value={expenseTable[index].budget_levels?.[idx] || ""}
                              onChange={(e) => handleExpenseChange(index, "budget_levels", e.target.value, idx)}
                              disabled={isFormLoading}
                            >
                              <option value={""} disabled>
                                Выберите уровень
                              </option>
                              {chosenLevels(expense.budget!)
                                .filter((lv) => lv.level_number === lvl.num)
                                .map((opt, index) => (
                                  <option key={index} value={opt.budget_type_id?.toString()} disabled={!!!opt.status}>
                                    {opt.budget_type}
                                  </option>
                                ))}
                            </select>
                          ) : (
                            <span className="text-secondary">Уровень {lvl.num} отстутствует</span>
                          )}
                        </td>
                      )
                    )}

                    <td>
                      <FormattedNumberInput
                        className="form-control"
                        style={{ width: 100 }}
                        value={expense.budget_balance || 0}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>

                    <td>
                      <FormattedNumberInput
                        className="form-control"
                        style={{ width: 100 }}
                        value={expense.budget_balance! - expense.quantity! * expense.price! || 0}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <div className="btn-list flex-nowrap">
                        <ActionButton
                          icon={<IconMinus className="icon" />}
                          title="Удалить строку"
                          action={() => handleDeleteRow(index)}
                          color={"danger"}
                          modal={false}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="col-lg-12 d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-outline-primary mb-3"
          disabled={isFormLoading || !isBudgetCalculable}
          onClick={handleGetBudgetBalance}
        >
          Рассчитать бюджет
        </button>
      </div>

      {paymentFields.slice(9).map(renderField)}
    </form>
  );

  const newPaymentFooter = (
    <div className="d-flex gap-3">
      <div className="alert alert-info m-0" style={{ padding: "7px 16px" }} role="alert">
        <div className="text-secondary">Перед отправкой заявки на оплату необходимо рассчитать бюджет</div>
      </div>

      <button
        type="submit"
        form="paymentForm"
        className="btn btn-success d-flex gap-1"
        disabled={isFormLoading || !isBudgetCalculated}
      >
        Отправить заявку
        {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
      </button>
    </div>
  );

  return (
    <ModalComponent
      header="Заявка на оплату"
      body={newPaymentBody}
      footer={newPaymentFooter}
      disableClose={isFormLoading}
      shouldModalClose={isFormSubmitted}
    />
  );
}
